import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  toggleAllTypeSwitchesAction,
  toggleSingleTypeSwitchAction,
} from '../../../../redux/slices/analyses';
import { assertNotNil } from '../../../../utils/types';
import { ALL_INCIDENTS_OPTION_NAME, IncidentType } from '../../../IncidentType';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import ChartOptionsSwitch from '../ChartOptionsSwitch/ChartOptionsSwitch';
import ThinDivider from '../../../ThinDivider';
import { floatingComponentStyles } from '../../../../styles/shareableStyles';
import { useChartContext } from '../../ChartContext';

interface IProps {
  analysisId: string;
}

const Container = styled.div`
  ${floatingComponentStyles};
`;

const IncidentTypeSwitches = ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();

  const {
    settings: { chartSettings },
  } = useChartContext();
  const { activeIncidentTypes } = chartSettings;

  const incidentTypes = useAppSelector(
    (state) => state.incidents?.incidentTypes,
  );
  const isAllSwitchesOn = useMemo(
    () => activeIncidentTypes?.showAll,
    [activeIncidentTypes],
  );

  const toggleSingleSwitch = useCallback(
    (typeKey: string) => {
      dispatch(toggleSingleTypeSwitchAction({ typeKey, analysisId }));
    },
    [dispatch, analysisId],
  );

  const toggleAllSwitches = useCallback(() => {
    dispatch(toggleAllTypeSwitchesAction(analysisId));
  }, [dispatch, analysisId]);

  return (
    <Container>
      <ChartOptionsSwitch
        onClick={toggleAllSwitches}
        isChecked={isAllSwitchesOn}
        hasCustomColor={true}
      >
        <IncidentType
          name={ALL_INCIDENTS_OPTION_NAME}
          disabled={!isAllSwitchesOn}
        />
      </ChartOptionsSwitch>
      <ThinDivider layout="horizontal" size="full" />
      {Object.values(incidentTypes).map((incidentType) => {
        const currentIncidentType = incidentTypes[incidentType.id];
        assertNotNil(currentIncidentType.icon);

        const isActive = activeIncidentTypes[incidentType.id];

        return (
          <ChartOptionsSwitch
            key={incidentType.id}
            onClick={() => toggleSingleSwitch(incidentType.id)}
            isChecked={isActive}
            hasCustomColor={true}
          >
            <IncidentType name={incidentType.name} disabled={!isActive} />
          </ChartOptionsSwitch>
        );
      })}
    </Container>
  );
};

export default IncidentTypeSwitches;
